import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription, forkJoin } from 'rxjs';
import { mensajeErrorAccion, mensajeErrorCarga } from '../../core/http-errors';
import { CatalogosHistoricoService, OpcionEjercicio, textoTipoAsociacion } from '../../services/catalogos-historico.service';
import { CambiosHistorico, HistoricoApiService, RelacionHistorico } from '../../services/historico-api.service';
import { AlertService } from '../alert/alert.service';
import { AlertButtonType } from '../dialog-alert/AlertButtonType.enum';
import { DialogAlertService } from '../dialog-alert/dialog-alert.service';
import { OpcionBusqueda, SelectorBusquedaComponent } from '../selector-busqueda/selector-busqueda.component';

/**
 * Edición administrativa de UN registro del histórico (P1.3 Wave 5). Campos editables: asociación, cargo, ejercicio y el tipo entre
 * «Altas» y «Cambios» (la persona y el estado no se cambian aquí: el estado sigue gestionándose con el alta y la baja). Solo se editan
 * relaciones vigentes o de ejercicios anteriores (no bajas). Antes de guardar se resume el cambio y se pide confirmación; la API rechaza duplicados, comprueba las reglas del
 * cargo (exclusivo, máximo, disponibilidad) y detecta si otra persona modificó el registro mientras tanto (409). Queda auditado.
 */
@Component({
  selector: 'app-historico-editar',
  standalone: true,
  imports: [CommonModule, FormsModule, SelectorBusquedaComponent],
  templateUrl: './historico-editar.component.html'
})
export class HistoricoEditarComponent implements OnInit, OnDestroy {
  @Input({ required: true }) relacion!: RelacionHistorico;
  @Output() guardado = new EventEmitter<void>();
  @Output() cancelado = new EventEmitter<void>();

  asociaciones: OpcionBusqueda[] = [];
  cargos: OpcionBusqueda[] = [];
  ejercicios: OpcionEjercicio[] = [];
  cargando = true;
  errorCarga = '';

  asociacionId!: number;
  cargoId!: number;
  ejercicioId!: number;
  tipoId!: number;

  guardando = false;
  error = '';

  private readonly suscripciones = new Subscription();

  constructor(
    private readonly api: HistoricoApiService,
    private readonly catalogos: CatalogosHistoricoService,
    private readonly dialogo: DialogAlertService,
    private readonly alertas: AlertService
  ) {}

  /** Se editan las relaciones vigentes y las de ejercicios cerrados; las bajas y las desactivadas se conservan como están. */
  get editable(): boolean { return this.relacion.estado === 'vigente' || this.relacion.estado === 'historico'; }
  get tipoEditable(): boolean { return this.editable && [1, 2].includes(this.relacion.idTipoHistorico); }
  get hayCambios(): boolean {
    return this.asociacionId !== this.relacion.idAsociacion || this.cargoId !== this.relacion.idCargo
      || this.ejercicioId !== this.relacion.idEjercicio || this.tipoId !== this.relacion.idTipoHistorico;
  }
  get puedeGuardar(): boolean { return this.editable && this.hayCambios && !this.guardando && !this.cargando; }

  ngOnInit(): void {
    this.reiniciar();
    this.cargar();
  }

  ngOnDestroy(): void { this.suscripciones.unsubscribe(); }

  private reiniciar(): void {
    this.asociacionId = this.relacion.idAsociacion;
    this.cargoId = this.relacion.idCargo;
    this.ejercicioId = this.relacion.idEjercicio;
    this.tipoId = this.relacion.idTipoHistorico;
    this.error = '';
  }

  cargar(): void {
    this.cargando = true;
    this.errorCarga = '';
    this.suscripciones.add(forkJoin([this.catalogos.asociacionesOrdenadas(), this.catalogos.cargosOrdenados(), this.catalogos.ejerciciosRecientes()]).subscribe({
      next: ([asociaciones, cargos, ejercicios]) => {
        this.asociaciones = asociaciones.map(a => ({ id: a.id, texto: a.nombre, detalle: textoTipoAsociacion(a.tipo), grupo: a.tipo, inactiva: !a.activa }));
        this.cargos = cargos.map(c => ({ id: c.id, texto: c.nombre + (c.esInfantil ? ' (infantil)' : ''), grupo: c.esInfantil ? 1 : 0, inactiva: !c.activo }));
        this.ejercicios = ejercicios;
        this.cargando = false;
      },
      error: (err: HttpErrorResponse) => { this.errorCarga = mensajeErrorCarga(err, 'los catálogos'); this.cargando = false; }
    }));
  }

  elegirAsociacion(id: number | null): void { if (id !== null) this.asociacionId = id; }
  elegirCargo(id: number | null): void { if (id !== null) this.cargoId = id; }

  private nombreAsociacion(id: number): string { return this.asociaciones.find(a => a.id === id)?.texto ?? String(id); }
  private nombreCargo(id: number): string { return this.cargos.find(c => c.id === id)?.texto ?? String(id); }
  private anyo(id: number): string { return String(this.ejercicios.find(e => e.id === id)?.anyo ?? id); }

  /** Cambios que se van a guardar, uno por línea («Campo: antes → después»). */
  resumenCambios(): string[] {
    const r = this.relacion;
    const lineas: string[] = [];
    if (this.asociacionId !== r.idAsociacion) lineas.push(`Asociación: ${r.nombreAsociacion} → ${this.nombreAsociacion(this.asociacionId)}`);
    if (this.cargoId !== r.idCargo) lineas.push(`Cargo: ${r.cargo} → ${this.nombreCargo(this.cargoId)}`);
    if (this.ejercicioId !== r.idEjercicio) lineas.push(`Ejercicio: ${r.anyoEjercicio} → ${this.anyo(this.ejercicioId)}`);
    if (this.tipoId !== r.idTipoHistorico) lineas.push(`Tipo: ${r.idTipoHistorico === 1 ? 'Altas' : 'Cambios'} → ${this.tipoId === 1 ? 'Altas' : 'Cambios'}`);
    return lineas;
  }

  guardar(): void {
    if (!this.puedeGuardar) return;
    const r = this.relacion;
    const vigente = r.ejercicioVigente === 1 || this.ejercicios.find(e => e.id === this.ejercicioId)?.vigente;
    const texto = `Vas a modificar un registro del histórico de ${r.nombre} ${r.apellidos}:\n\n${this.resumenCambios().join('\n')}\n\n`
      + (vigente ? 'ATENCIÓN: afecta al ejercicio vigente; puede cambiar los permisos que concede el cargo.\n\n' : '')
      + 'La operación queda registrada en la auditoría. ¿Quieres continuar?';
    this.suscripciones.add(this.dialogo.openDialogAlert({
      title: 'Editar registro del histórico', content: texto, buttonsAlert: [AlertButtonType.Cancelar, AlertButtonType.Aceptar]
    }).afterClosed().subscribe(respuesta => { if (respuesta === AlertButtonType.Aceptar) this.aplicar(); }));
  }

  private aplicar(): void {
    if (this.guardando) return;
    const r = this.relacion;
    const cambios: CambiosHistorico = { esperado: { active: r.active, idTipoHistorico: r.idTipoHistorico } };
    if (this.asociacionId !== r.idAsociacion) cambios.idAsociacion = this.asociacionId;
    if (this.cargoId !== r.idCargo) cambios.idCargo = this.cargoId;
    if (this.ejercicioId !== r.idEjercicio) cambios.ejercicio = this.ejercicioId;
    if (this.tipoId !== r.idTipoHistorico) cambios.idTipoHistorico = this.tipoId;
    this.guardando = true;
    this.error = '';
    this.suscripciones.add(this.api.editar({ idAsociacion: r.idAsociacion, idAsociado: r.idAsociado, idCargo: r.idCargo, ejercicio: r.idEjercicio }, cambios).subscribe({
      next: () => { this.guardando = false; this.alertas.success('El registro del histórico se ha actualizado.', 5000); this.guardado.emit(); },
      error: (err: HttpErrorResponse) => { this.guardando = false; this.error = mensajeErrorAccion(err, 'No se ha podido guardar el cambio.'); }
    }));
  }

  cancelar(): void { if (!this.guardando) this.cancelado.emit(); }
}
